import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, AlertTriangle } from 'lucide-react';
import { AnkhSpinner } from '@/components/common/AnkhSpinner';

const API_BASE = import.meta.env.VITE_API_URL || '';

type Status = 'loading' | 'success' | 'error';

export default function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const uid = searchParams.get('uid') ?? '';
  const sig = searchParams.get('sig') ?? '';

  const [status, setStatus] = useState<Status>('loading');
  const [errorMsg, setErrorMsg] = useState<string>('');

  useEffect(() => {
    if (!uid || !sig) {
      setErrorMsg(
        'This unsubscribe link is missing required parameters. Please use the link from your email.'
      );
      setStatus('error');
      return;
    }

    let cancelled = false;

    fetch(`${API_BASE}/api/email/unsubscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid, sig }),
    })
      .then(async (res) => {
        if (cancelled) return;
        if (res.ok) {
          setStatus('success');
        } else {
          const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
          const msg = typeof body['message'] === 'string' ? body['message'] : '';
          setErrorMsg(msg || 'This unsubscribe link is invalid or has already been used.');
          setStatus('error');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setErrorMsg('Something went wrong. Please try again or contact tavian@polymyth.quest.');
          setStatus('error');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [uid, sig]);

  return (
    <div className="mx-auto max-w-md px-6 py-20 min-h-[70vh] flex items-center justify-center">
      <div className="w-full text-center space-y-6 rounded-2xl border border-border/50 bg-background/60 backdrop-blur-md p-8 shadow-xl">
        {status === 'loading' && (
          <>
            <AnkhSpinner size="lg" />
            <p className="text-muted-foreground">Unsubscribing...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="flex justify-center">
              <CheckCircle className="h-12 w-12 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">You've been unsubscribed</h1>
            <p className="text-muted-foreground">You've been unsubscribed from Polymyth emails.</p>
            <p className="text-sm text-muted-foreground">
              Changed your mind?{' '}
              <Link to="/dashboard" className="text-primary hover:underline">
                You can re-subscribe anytime in your account settings.
              </Link>
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="flex justify-center">
              <AlertTriangle className="h-12 w-12 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">Couldn't unsubscribe</h1>
            <p className="text-muted-foreground">{errorMsg}</p>
            <p className="text-sm text-muted-foreground">
              Need help?{' '}
              <a href="mailto:tavian@polymyth.quest" className="text-primary hover:underline">
                Contact us
              </a>
              .
            </p>
          </>
        )}
      </div>
    </div>
  );
}
